import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import useCarousel from './hooks/useCarousel';
import CarouselTrack from './CarouselTrack';
import CarouselControls from './CarouselControls';
import { Problem } from './types';

interface CarouselProps {
  items: Problem[];
}

export default function Carousel({ items }: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const {
    activeIndex,
    canScroll,
    handleNext,
    handlePrevious,
    handleScroll,
    scrollToIndex
  } = useCarousel({ trackRef, itemsCount: items.length });

  useEffect(() => {
    // Center first item on mount
    const timer = setTimeout(() => {
      scrollToIndex(0);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative -mx-4 sm:-mx-6 lg:-mx-8">
      <div className="px-0">
        <div className="relative"> {/* Add wrapper for proper stacking context */}
          <CarouselTrack
            ref={trackRef}
            items={items}
            activeIndex={activeIndex}
            onScroll={handleScroll}
            onItemClick={scrollToIndex}
          />

          {/* Gradient overlays with lower z-index */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: canScroll.left ? 1 : 0 }}
            className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white to-transparent pointer-events-none z-[1]"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: canScroll.right ? 1 : 0 }}
            className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-white to-transparent pointer-events-none z-[1]"
          />
        </div>

        <CarouselControls
          onPrevious={handlePrevious}
          onNext={handleNext}
          canGoNext={canScroll.right}
          canGoPrevious={canScroll.left}
        />
      </div>
    </div>
  );
}